import { MutableRefObject, useRef } from 'react';
import { JobPhaseReceivableSheetInterface } from '@vatos-pas/common';

import { useReceivableId, useReceivableRegistry } from './ReceivableRegistry';
import {
  ReceivableChanges,
  useReceivablePersistence,
} from './useReceivablePersistence';

/**
 * Shared optimistic-write core for the editable receivable cells
 * (SupplyPro / Billed / Invoice).
 *
 * The row's jobPhaseReceivableId is owned by the ReceivableRegistry and shared by
 * all three cells, so the first edit on a not-yet-created row creates exactly one
 * receivable and the siblings reuse its id:
 *
 * - `receivableId` is the effective id (the record's, or the one created this
 *   session via the registry). Pass it to the history modal so a sibling sees the
 *   new id without a refetch.
 * - `pendingRef` is true for the duration of THIS cell's own save. The cell guards
 *   its own "resync display state from `record`" effect with it, so an incoming
 *   `record` re-render (which may originate from a sibling cell's save updating the
 *   shared row) can't clobber this cell's in-flight optimistic edit. Each cell has
 *   its own pendingRef; it does not coordinate across cells.
 * - `commit` updates when an id is known, otherwise creates exactly once through
 *   the registry; a cell that loses the create race applies its change via a
 *   follow-up update. Rethrows on failure so the caller can revert + notify.
 */
export const useOptimisticReceivable = (
  record: JobPhaseReceivableSheetInterface | undefined,
): {
  receivableId: string | null;
  pendingRef: MutableRefObject<boolean>;
  commit: (changes: ReceivableChanges) => Promise<void>;
} => {
  const { save } = useReceivablePersistence();
  const registry = useReceivableRegistry();
  const pendingRef = useRef(false);

  const receivableId = useReceivableId(record);

  const commit = async (changes: ReceivableChanges): Promise<void> => {
    if (!record) return;
    const current = {
      supplyProIntegrated: !!record.supplyProIntegrated,
      billed: !!record.billed,
      invoicePOAmount: record.invoicePOAmount,
      internalInvoiceNumber: record.internalInvoiceNumber,
    };
    const knownId =
      record.jobPhaseReceivableId ?? registry.peekId(record.jobPhaseId);

    pendingRef.current = true;
    try {
      if (knownId) {
        await save({
          jobPhaseId: record.jobPhaseId,
          jobPhaseReceivableId: knownId,
          changes,
          current,
        });
        return;
      }

      const { id, created } = await registry.ensureCreated(
        record.jobPhaseId,
        async () => {
          const saved = await save({
            jobPhaseId: record.jobPhaseId,
            jobPhaseReceivableId: null,
            changes,
            current,
          });
          return saved.id;
        },
      );

      // Lost the create race: the winning create didn't include our change, so
      // apply it now against the shared id.
      if (!created) {
        await save({
          jobPhaseId: record.jobPhaseId,
          jobPhaseReceivableId: id,
          changes,
          current,
        });
      }
    } finally {
      pendingRef.current = false;
    }
  };

  return { receivableId, pendingRef, commit };
};
